import React, { CSSProperties, useContext } from "react"
import { useTranslation } from "react-i18next"
import { Container } from "rsuite"
import { useSignalEffect } from "@preact/signals-react/runtime"
import { nl2br } from "@/shared"
import error from '@client/stores/errorStore'
import toastMessage from '@client/utils/toastMessage'
import { errorNoticeDuration } from '@client/config/constants'

type ErrorProviderProps = { children: any }
type PanelErrorProps = { message: string, showTitle?: boolean, style?: CSSProperties }

const ErrorContext = React.createContext({})

export function useError() {
  return useContext(ErrorContext)
}

//
// A message from the server appears where it arises (operator, 14.10.2026).
//
// The store used to draw nothing: nobody subscribed to it, so its full page
// came at some later render, or never. Now a form or dialog with its own error
// field takes the message (error.take()); whatever is left once the calls of
// this turn have run is shown here as a notice — over the page, which stays.
//
function ErrorProvider({ children }: ErrorProviderProps) {
  const { t } = useTranslation()

  // An effect, not a subscribed render: nothing here needs drawing again, and
  // a subscribed render (useSignals) also tracked what rendered after it.
  useSignalEffect(() => {
    if (!error.errorCount.value) return
    // after the caller had its chance: it resumes in a microtask, this runs later
    const timer = setTimeout(() => {
      const message = error.take()
      if (message) toastMessage(t(message) || message, 'error', errorNoticeDuration)
    }, 0)
    return () => clearTimeout(timer)
  })

  return (
    <ErrorContext.Provider value={{}} >
      {children}
    </ErrorContext.Provider>
  )
}
export default ErrorProvider

//
// panel error
//

const panelErrorStyle = {
  background: 'var(--color-error-bg)',
  border: '1px solid var(--color-error-border)',
  color: 'var(--color-error-text)',
  marginTop: '1em',
  padding: '1em',
  flex: 'initial',
}

export const PanelError = ({ message, showTitle = true, style = {} }: PanelErrorProps) => {
  const { t } = useTranslation()
  const _style = { ...panelErrorStyle, ...style }

  return (
    <Container style={_style} >
      {showTitle && <p style={{ fontWeight: 'bold' }}> {t('An error has occurred:')}  </p>}
      <p> {nl2br(message, true)} </p>
    </Container>
  )
}
